import { css } from '@compiled/react';

import Modal, {
	ModalBody,
	ModalFooter,
	ModalHeader,
	ModalTransition,
} from '@atlaskit/modal-dialog';
import { token } from '@atlaskit/tokens';

import { LoomOnboardingButton } from './loom-onboarding-button';
import { LoomOnboardingHeading } from './loom-onboarding-heading';

import type { Recorder } from '../utilities/types';

/**
 * Mirrors the `LoomOnboardingText` (size `sm`) styles so the `<ol>` markers
 * (the "1.", "2.", …) render in the same font/color as the list text. The
 * `<li>` children inherit these styles from the `<ol>`, so they do not need
 * to be wrapped in `LoomOnboardingText` individually.
 */
const DOWNLOAD_MAC_HELPER_MODAL_TEST_ID = 'download-mac-helper-modal';

const instructionsListCss = css({
	margin: 0,
	paddingInlineStart: token('space.300'),
	fontFamily: 'var(--atlassian-sans-font-family)',
	color: token('color.text'),
	fontSize: '0.875rem',
	lineHeight: '1.25rem',
	textAlign: 'left',
});

export const DownloadMacHelperModal = ({
	isOpen,
	handleClose,
}: {
	isOpen: boolean;
	/**
	 * Called when the modal closes. Receives the selected `Recorder`
	 * when the user picked a Mac variant, or `undefined` when they
	 * dismissed the modal (X button, ESC, overlay click).
	 */
	handleClose: (recorder?: Recorder) => void;
}) => {
	return (
		<ModalTransition>
			{isOpen && (
				<>
					{/*
					 * The installed @atlaskit/modal-dialog@16.2.2 hard-codes
					 * `border-radius: var(--ds-radius-small, 3px)` on the
					 * dialog. The 12px (`radius.xlarge`) branch is gated
					 * behind the `platform-dst-shape-theme-default` FG which
					 * is off in WAC. Modal renders in a portal, so we can't
					 * override via `css` from a parent — this scoped <style>
					 * targets the dialog by its testId.
					 */}
					<style>{`
                        [data-testid="${DOWNLOAD_MAC_HELPER_MODAL_TEST_ID}"] {
                            border-radius: 12px;
                        }
                        [data-testid="${DOWNLOAD_MAC_HELPER_MODAL_TEST_ID}--footer"] {
                            padding-top: var(--ds-space-300, 24px);
                        }
                    `}</style>
					<Modal
						onClose={() => handleClose()}
						width="medium"
						label="Select the version that's right for you"
						testId={DOWNLOAD_MAC_HELPER_MODAL_TEST_ID}
					>
						<ModalHeader hasCloseButton>
							<LoomOnboardingHeading level={3}>
								Select the version that's right for you
							</LoomOnboardingHeading>
						</ModalHeader>
						<ModalBody>
							<ol css={instructionsListCss}>
								<li>At the top left, open the Apple menu.</li>
								<li>Select About This Mac.</li>
								<li>{`In the "Overview" tab, look for "Processor" or "Chip".`}</li>
								<li>{`Check if it says "Intel" or "Apple".`}</li>
							</ol>
						</ModalBody>
						<ModalFooter>
							<LoomOnboardingButton appearance="primary" onClick={() => handleClose('intel')}>
								MacOS - Intel
							</LoomOnboardingButton>
							<LoomOnboardingButton appearance="default" onClick={() => handleClose('apple')}>
								MacOS - Apple Silicon
							</LoomOnboardingButton>
						</ModalFooter>
					</Modal>
				</>
			)}
		</ModalTransition>
	);
};
